import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/cartpage.scss";

const FALLBACK_SHIPPING_RATE = 15;
const FALLBACK_FREE_SHIPPING_THRESHOLD = 250;

const parsePrice = (price) => {
  const value = Number(String(price ?? 0).replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(value) ? value : 0;
};

const getShippingNumber = (keys, fallback) => {
  const shipping = siteConfig?.shipping || {};
  const value = keys.map((key) => shipping[key]).find((item) => item != null);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const shippingRate = getShippingNumber(
  ["flatRate", "standardRate", "rate", "cost"],
  FALLBACK_SHIPPING_RATE
);
const freeShippingThreshold = getShippingNumber(
  ["freeThreshold", "freeShippingThreshold", "threshold"],
  FALLBACK_FREE_SHIPPING_THRESHOLD
);

const formatMoney = (value) =>
  new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
    style: "currency",
    currency: siteConfig.currency?.code || "USD",
  }).format(value);

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  const items = cartItems
    .map((item) => ({
      ...item,
      product: productData.find(
        (product) => String(product.id) === String(item.id)
      ),
    }))
    .filter((item) => item.product);

  const subtotal = items.reduce(
    (total, item) =>
      total + parsePrice(item.product.price) * Number(item.quantity || 0),
    0
  );
  const itemCount = items.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );
  const qualifiesForFreeShipping = subtotal >= freeShippingThreshold;
  const shippingCost = qualifiesForFreeShipping ? 0 : shippingRate;
  const total = subtotal + shippingCost;
  const amountUntilFreeShipping = Math.max(
    freeShippingThreshold - subtotal,
    0
  );
  const progress = Math.min(
    (subtotal / Math.max(freeShippingThreshold, 1)) * 100,
    100
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCheckout = () => {
    if (!items.length) return;
    navigate("/checkout");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!items.length) {
    return (
      <main className="cart-page cart-page--empty">
        <section className="cart-page__empty" aria-labelledby="empty-cart-title">
          <p className="cart-page__eyebrow">Your bag</p>
          <h1 id="empty-cart-title">Your cart is empty</h1>
          <p>
            Explore enduring knitwear, outerwear and everyday layers built to be
            worn for years.
          </p>
          <Link
            to="/shop"
            onClick={scrollToTop}
            className="cart-page__primary-link"
          >
            Shop the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <p className="cart-page__eyebrow">Your selection</p>
        <h1>Shopping cart</h1>
        <p>
          {itemCount} {itemCount === 1 ? "item" : "items"}
        </p>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Items in your cart">
          {items.map((item) => {
            const { product } = item;
            const image = product.images?.[0] || product.image;
            const lineTotal = parsePrice(product.price) * item.quantity;
            const itemKey = `${item.id}-${item.size || "default"}-${
              item.color || "default"
            }`;

            return (
              <article className="cart-item" key={itemKey}>
                <Link
                  to={`/shopDetails/${product.id}`}
                  className="cart-item__image-link"
                  onClick={scrollToTop}
                  aria-label={`View ${product.name}`}
                >
                  <img
                    src={image}
                    alt={product.name}
                    className="cart-item__image"
                  />
                </Link>

                <div className="cart-item__details">
                  <p className="cart-item__category">{product.category}</p>
                  <h2>
                    <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                      {product.name}
                    </Link>
                  </h2>
                  <dl className="cart-item__options">
                    {item.color && (
                      <div>
                        <dt>Color</dt>
                        <dd>{item.color}</dd>
                      </div>
                    )}
                    {item.size && (
                      <div>
                        <dt>Size</dt>
                        <dd>{item.size}</dd>
                      </div>
                    )}
                  </dl>

                  <button
                    type="button"
                    className="cart-item__remove"
                    onClick={() =>
                      removeFromCart(item.id, item.size, item.color)
                    }
                    aria-label={`Remove ${product.name} from cart`}
                  >
                    Remove
                  </button>
                </div>

                <div className="cart-item__purchase">
                  <div
                    className="cart-item__quantity"
                    role="group"
                    aria-label={`Quantity for ${product.name}`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(
                          item.id,
                          item.size,
                          item.color,
                          item.quantity - 1
                        )
                      }
                      disabled={item.quantity <= 1}
                      aria-label={`Decrease quantity of ${product.name}`}
                    >
                      −
                    </button>
                    <span aria-live="polite">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(
                          item.id,
                          item.size,
                          item.color,
                          item.quantity + 1
                        )
                      }
                      disabled={item.quantity >= Number(product.stock)}
                      aria-label={`Increase quantity of ${product.name}`}
                    >
                      +
                    </button>
                  </div>
                  <p className="cart-item__price">{formatMoney(lineTotal)}</p>
                </div>
              </article>
            );
          })}

          <Link
            to="/shop"
            onClick={scrollToTop}
            className="cart-page__continue"
          >
            <span aria-hidden="true">←</span> Continue shopping
          </Link>
        </section>

        <aside className="cart-summary" aria-labelledby="order-summary-title">
          <h2 id="order-summary-title">Order summary</h2>

          <div className="cart-summary__shipping-message" aria-live="polite">
            <p>
              {qualifiesForFreeShipping
                ? "Your order qualifies for free shipping."
                : `${formatMoney(
                    amountUntilFreeShipping
                  )} away from free shipping.`}
            </p>
            <div
              className="cart-summary__progress"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.round(progress)}
              aria-label="Free shipping progress"
            >
              <span style={{ width: `${progress}%` }} />
            </div>
          </div>

          <dl className="cart-summary__totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatMoney(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{shippingCost === 0 ? "Free" : formatMoney(shippingCost)}</dd>
            </div>
            <div className="cart-summary__total">
              <dt>Estimated total</dt>
              <dd>{formatMoney(total)}</dd>
            </div>
          </dl>

          <p className="cart-summary__note">
            This demo total does not collect sales tax or import duties.
          </p>
          <button
            type="button"
            className="cart-summary__checkout"
            onClick={handleCheckout}
          >
            Proceed to checkout
          </button>
          <p className="cart-summary__secure">Secure, simulated checkout</p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
